import { Controller } from "react-hook-form";
import Button from "components/button";
import Icon from "components/icon";
import Input from "components/input";
import Select from "components/select";
import Typography from "components/typography";
import ConfirmationModal from "components/shared/confirmation-modal";
import useAddFreezerModelForm from "./useAddFreezerModelForm";

export const addFreezerModelPageRouteName =
  "/master-data/freezer-model/add";

const AddFreezerModelPage = () => {
  const {
    control,
    register,
    errors,
    fields,
    editingIndex,
    showSaveModal,
    setShowSaveModal,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    t,
    brandOptions,
    handleCreateBrand,
  } = useAddFreezerModelForm();

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-6">
        {/* Left Panel - Form */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1 mb-6">
            <Typography variant="heading4" as="h2">
              {t("section-title")}
            </Typography>
            <Typography variant="body" tone="muted">
              {editingIndex !== null
                ? t("section-subtitle-edit")
                : t("section-subtitle-add")}
            </Typography>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleAddToList();
            }}
            className="flex flex-col gap-4"
          >
            {/* Freezer Brand */}
            <Controller
              name="freezerBrandId"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-freezer-brand")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={brandOptions}
                    value={field.value}
                    onValueChange={(val) => field.onChange(val)}
                    onCreateOption={handleCreateBrand}
                    error={fieldState.error}
                    placeholder={t("label-freezer-brand")}
                    data-testid="select-freezer-brand"
                  />
                </div>
              )}
            />

            {/* Freezer Model Name */}
            <Input
              label={t("label-freezer-model-name")}
              placeholder={t("label-freezer-model-name")}
              required
              {...register("freezerModelName")}
              error={errors.freezerModelName}
              data-testid="input-freezer-model-name"
            />

            {/* Fuel Efficiency */}
            <Input
              label={t("label-fuel-efficiency")}
              placeholder={t("label-fuel-efficiency")}
              type="number"
              enableNumberFormat
              required
              {...register("fuelEfficiency")}
              error={errors.fuelEfficiency}
              rightIcon={
                <Typography variant="body" tone="muted" as="span">
                  {t("fuel-efficiency-unit")}
                </Typography>
              }
              data-testid="input-fuel-efficiency"
            />

            {/* Status */}
            <Controller
              name="status"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-status")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={[
                      { label: t("status-active"), value: "active" },
                      { label: t("status-inactive"), value: "inactive" },
                    ]}
                    value={field.value}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-freezer-model-status"
                  />
                </div>
              )}
            />

            {editingIndex !== null ? (
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  color="tertiary"
                  size="medium"
                  isFullSize
                  onClick={handleDiscardChanges}
                  data-testid="btn-discard-changes-freezer"
                >
                  {t("btn-discard-changes")}
                </Button>
                <Button
                  type="submit"
                  variant="fill"
                  color="primary"
                  size="medium"
                  isFullSize
                  data-testid="btn-save-changes-freezer"
                >
                  {t("btn-save-changes")}
                </Button>
              </div>
            ) : (
              <Button
                type="submit"
                variant="fill"
                color="primary"
                size="medium"
                isFullSize
                data-testid="btn-add-to-list-freezer"
              >
                {t("btn-add-to-list")}
              </Button>
            )}
          </form>
        </section>

        {/* Right Panel - Items List */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1 mb-6">
            <Typography variant="heading4" as="h2">
              {t("list-title")}
            </Typography>
            <Typography variant="body" tone="muted">
              {t("list-subtitle")}
            </Typography>
          </div>

          {fields.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Icon
                name="document-text"
                size={48}
                className="text-greyScale-60 mb-4"
              />
              <Typography variant="body" tone="muted">
                {t("no-data-title")}
              </Typography>
              <Typography variant="body" tone="muted">
                {t("no-data-subtitle")}
              </Typography>
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-h-[500px] overflow-y-auto">
              {fields.map((item, index) => {
                const brandOption = brandOptions.find(b => b.value === item.freezerBrandId);
                return (
                  <div
                    key={item.id}
                    className={`flex items-center justify-between rounded-xl border p-4 ${
                      editingIndex === index
                        ? "border-primary-500 bg-primary-50"
                        : "border-border"
                    }`}
                  >
                    <div className="flex flex-col gap-1">
                      <Typography variant="bodyLarge" className="font-semibold">
                        {item.freezerModelName}
                      </Typography>
                      <div className="flex items-center gap-2">
                        <Typography variant="body" tone="muted">
                          {t("label-freezer-brand")}
                        </Typography>
                        <Typography variant="body" tone="muted">
                          :
                        </Typography>
                        <Typography variant="body">
                          {brandOption?.label ?? item.freezerBrandId}
                        </Typography>
                      </div>
                    <div className="flex items-center gap-2">
                      <Typography variant="body" tone="muted">
                        {t("label-fuel-efficiency")}
                      </Typography>
                      <Typography variant="body" tone="muted">
                        :
                      </Typography>
                      <Typography variant="body">
                        {item.fuelEfficiency} {t("fuel-efficiency-unit")}
                      </Typography>
                    </div>
                    <div className="flex items-center gap-2">
                      <Typography variant="body" tone="muted">
                        {t("label-status")}
                      </Typography>
                      <Typography variant="body" tone="muted">
                        :
                      </Typography>
                      <Typography
                        variant="body"
                        className="capitalize"
                      >
                        {item.status}
                      </Typography>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleEditItem(index)}
                      className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors cursor-pointer ${
                        editingIndex === index
                          ? "bg-primary-500 text-white"
                          : "hover:bg-greyScale-90 text-primary-500"
                      }`}
                      data-testid={`btn-edit-row-freezer-${index}`}
                    >
                      <Icon name="edit-2" size={16} />
                    </button>
                    {fields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleDeleteItem(index)}
                        className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-error-50 text-error-500 transition-colors cursor-pointer"
                        data-testid={`btn-delete-row-freezer-${index}`}
                      >
                        <Icon name="trash" size={16} />
                      </button>
                    )}
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Bottom Action Bar */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          onClick={handleCancel}
          data-testid="btn-cancel-freezer"
        >
          {t("btn-cancel")}
        </Button>
        <Button
          type="button"
          variant="fill"
          color="primary"
          size="medium"
          onClick={() => setShowSaveModal(true)}
          disabled={fields.length === 0}
          data-testid="btn-save-freezer"
        >
          {t("btn-save")}
        </Button>
      </div>

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title")}
        message={t("modal-save-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleSave}
        onCancel={() => setShowSaveModal(false)}
        testId="save-freezer-modal"
      />
    </div>
  );
};

export default AddFreezerModelPage;
